/**
 * PDF Text Extraction Utilities
 * Uses pdf.js to extract text content from PDF files
 */

import * as pdfjsLib from 'pdfjs-dist';

// Set worker source for pdf.js
pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;

/**
 * Extract text content from a PDF file
 * @param {ArrayBuffer} pdfData - PDF file data
 * @returns {Promise<string>} - Extracted text content
 */
export const extractTextFromPDF = async (pdfData) => {
  try {
    const pdf = await pdfjsLib.getDocument({ data: pdfData }).promise;
    let fullText = '';
    
    // Extract text from all pages
    for (let i = 1; i <= pdf.numPages; i++) {
      const page = await pdf.getPage(i);
      const textContent = await page.getTextContent();
      const pageText = textContent.items.map(item => item.str).join(' ');
      fullText += pageText + ' ';
    }
    
    return fullText.trim();
  } catch (error) {
    console.error('Error extracting text from PDF:', error);
    throw new Error('Failed to extract text from PDF');
  }
};

/**
 * Extract serial number from PDF content using regex patterns
 * @param {string} text - PDF text content
 * @returns {Object} - Matched serial number info
 */
export const extractSerialFromPDFText = (text) => {
  const patterns = [
    // Serial Number patterns
    { regex: /Serial\s*(?:Number|No|#)?[:\s]*([A-Z0-9_-]{3,})/i, type: 'serial' },
    { regex: /S\/N[:\s]*([A-Z0-9_-]{3,})/i, type: 'serial' },
    { regex: /SN[:\s]*([A-Z0-9_-]{3,})/i, type: 'serial' },
    
    // Asset ID patterns
    { regex: /Asset\s*(?:ID|Number|No)?[:\s]*([A-Z0-9_-]{3,})/i, type: 'asset' },
    { regex: /Asset\s*Number[:\s]*([A-Z0-9_-]{3,})/i, type: 'asset' },
    
    // Equipment ID patterns
    { regex: /Equipment\s*(?:ID|Number)?[:\s]*([A-Z0-9_-]{3,})/i, type: 'equipment' },
    
    // Generic number patterns (fallback)
    { regex: /(?:ID|Number|No)[:\s]*([A-Z0-9_-]{5,})/i, type: 'generic' },
    
    // Standalone alphanumeric codes
    { regex: /\b([A-Z]{2,}\d{4,})\b/, type: 'code' },
    { regex: /\b(\d{4,}[A-Z]{2,})\b/, type: 'code' }
  ];
  
  for (const pattern of patterns) {
    const match = text.match(pattern.regex);
    if (match) {
      return {
        value: match[1].trim(),
        type: pattern.type,
        confidence: pattern.type === 'serial' || pattern.type === 'asset' ? 'high' : 'medium'
      };
    }
  }
  
  return null;
};

/**
 * Get PDF metadata (page count, etc.)
 * @param {ArrayBuffer} pdfData - PDF file data
 * @returns {Promise<Object>} - PDF metadata
 */
export const getPDFMetadata = async (pdfData) => {
  try {
    const pdf = await pdfjsLib.getDocument({ data: pdfData }).promise;
    return {
      pageCount: pdf.numPages,
      fingerprint: pdf.fingerprint || null
    };
  } catch (error) {
    console.error('Error getting PDF metadata:', error);
    return { pageCount: 0, fingerprint: null };
  }
};

/**
 * Convert PDF to base64 data URL
 * @param {File} file - PDF file
 * @returns {Promise<string>} - Base64 data URL
 */
export const fileToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
};

/**
 * Calculate simple hash for duplicate detection
 * @param {string} content - File content or base64 string
 * @returns {string} - Simple hash
 */
export const calculateHash = (content) => {
  let hash = 0;
  for (let i = 0; i < content.length; i++) {
    const char = content.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash;
  }
  return hash.toString(16);
};
